'use client'

import { Loader2, Save } from 'lucide-react'
import type React from 'react'
import { useEffect, useReducer } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { normalizeWhatsAppPhone } from '@/lib/phone'
import { PhoneInput } from '@/components/ui/phone-input'

export interface ContactFormData {
  firstName: string
  lastName?: string
  title?: string
  email?: string
  phoneNumber?: string
  fbMessengerId?: string
  telegramId?: string
  telegramUserId?: string
  instagramId?: string
  weChatId?: string
  lineId?: string
}

type ContactFormState = {
  firstName: string
  lastName: string
  title: string
  email: string
  phoneNumber: string
  fbMessengerId: string
  telegramId: string
  telegramUserId: string
  instagramId: string
  weChatId: string
  lineId: string
}

interface UnifiedContactFormProps {
  initialData?: Partial<ContactFormData>
  onSubmit: (data: ContactFormData) => Promise<void>
  onCancel?: () => void
  isLoading?: boolean
  mode: 'create' | 'edit'
  variant?: 'card' | 'dialog' | 'inline'
  errors?: Partial<Record<keyof ContactFormState, string>>
  showSubmitButton?: boolean
  submitButtonText?: string
  cancelButtonText?: string
  showLoadingSpinner?: boolean
  className?: string
  formError?: string | null
}

export function UnifiedContactForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  mode,
  variant = 'card',
  errors: externalErrors,
  showSubmitButton = true,
  submitButtonText,
  cancelButtonText,
  showLoadingSpinner = false,
  className,
  formError,
}: UnifiedContactFormProps) {
  // Use reducer for more stable state management
  const [internalFormData, setInternalFormData] = useReducer(
    (state: ContactFormState, newState: Partial<ContactFormState>) => ({
      ...state,
      ...newState,
    }),
    {
      firstName: initialData?.firstName ?? '',
      lastName: initialData?.lastName ?? '',
      title: initialData?.title ?? '',
      email: initialData?.email ?? '',
      phoneNumber: initialData?.phoneNumber ?? '',
      fbMessengerId: initialData?.fbMessengerId ?? '',
      telegramId: initialData?.telegramId ?? '',
      telegramUserId: initialData?.telegramUserId ?? '',
      instagramId: initialData?.instagramId ?? '',
      weChatId: initialData?.weChatId ?? '',
      lineId: initialData?.lineId ?? '',
    }
  )

  // Sync internal state when initialData changes (e.g., when switching between contacts)
  useEffect(() => {
    if (initialData) {
      setInternalFormData({
        firstName: initialData.firstName ?? '',
        lastName: initialData.lastName ?? '',
        title: initialData.title ?? '',
        email: initialData.email ?? '',
        phoneNumber: initialData.phoneNumber ?? '',
        fbMessengerId: initialData.fbMessengerId ?? '',
        telegramId: initialData.telegramId ?? '',
        telegramUserId: initialData.telegramUserId ?? '',
        instagramId: initialData.instagramId ?? '',
        weChatId: initialData.weChatId ?? '',
        lineId: initialData.lineId ?? '',
      })
    }
  }, [initialData])

  const errors = externalErrors || {}

  const renderFieldError = (field: keyof ContactFormState) => {
    const message = errors[field]
    if (!message) {
      return null
    }
    return <p className="text-red-600 text-sm">{message}</p>
  }

  // Use internal state for form display, but fall back to initialData for submit
  const formData = internalFormData

  const sanitize = (value: string): string | undefined => {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }

  // Helper to create input props with proper change handlers (no onBlur to prevent parent re-renders)
  const createInputProps = (field: keyof ContactFormState) => ({
    name: field,
    value: formData[field],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      handleInputChange(field, e.target.value),
    disabled: isLoading,
  })

  const handleInputChange = (field: keyof ContactFormState, value: string) => {
    // Update internal state immediately for responsive typing
    setInternalFormData({ [field]: value })

    // Clear internal error for this field when user starts typing
    if (errors[field]) {
      // Parent manages errors, so we don't touch them here
    }
  }

  const validateForm = (): boolean => {
    // Parent manages validation errors, so just return true
    // Parent component should handle validation logic
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Parent handles validation, so just proceed
    if (!validateForm()) {
      return
    }

    const payload: ContactFormData = {
      firstName: formData.firstName.trim(),
      lastName: sanitize(formData.lastName),
      title: sanitize(formData.title),
      email: sanitize(formData.email),
      phoneNumber: sanitize(normalizeWhatsAppPhone(formData.phoneNumber)),
      fbMessengerId: sanitize(formData.fbMessengerId),
      telegramId: sanitize(formData.telegramId),
      telegramUserId: sanitize(formData.telegramUserId),
      instagramId: sanitize(formData.instagramId),
      weChatId: sanitize(formData.weChatId),
      lineId: sanitize(formData.lineId),
    }

    try {
      await onSubmit(payload)
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const isFormValid = Boolean(formData.firstName.trim())

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
      {formError && (
        <Alert variant="destructive">
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            {...createInputProps('firstName')}
            placeholder="Enter first name"
            className={errors.firstName ? 'border-red-300' : ''}
          />
          {errors.firstName && (
            <p className="text-red-600 text-sm">{errors.firstName}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            {...createInputProps('lastName')}
            placeholder="Enter last name"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            {...createInputProps('title')}
            placeholder="Enter job title"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            {...createInputProps('email')}
            placeholder="Enter email address"
            className={errors.email ? 'border-red-300' : ''}
          />
          {errors.email && (
            <p className="text-red-600 text-sm">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phoneNumber">Phone Number</Label>
        <PhoneInput
          id="phoneNumber"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={value => handleInputChange('phoneNumber', value)}
          inputClassName={errors.phoneNumber ? 'border-red-300' : ''}
          placeholder="12-345-6789"
        />
        <p className="text-xs text-gray-500">
          Enter phone number with country code.
        </p>
        {errors.phoneNumber && (
          <p className="text-red-600 text-sm">{errors.phoneNumber}</p>
        )}
      </div>

      {/* Social Channels */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">
            Social & Messaging Handles
          </h3>
          <p className="text-xs text-gray-500">
            Provide any available social messaging identifiers to help agents
            reach this contact quickly.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Add at least one contact method so your team can follow up if they
            reach out again.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="fbMessengerId">Facebook Messenger ID</Label>
            <Input
              id="fbMessengerId"
              {...createInputProps('fbMessengerId')}
              placeholder="Enter Facebook Messenger ID"
              className={errors.fbMessengerId ? 'border-red-300' : ''}
            />
            {renderFieldError('fbMessengerId')}
          </div>

          <div className="space-y-2">
            <Label htmlFor="telegramId">Telegram Username</Label>
            <Input
              id="telegramId"
              {...createInputProps('telegramId')}
              placeholder="Enter Telegram username (@username)"
              className={errors.telegramId ? 'border-red-300' : ''}
            />
            {renderFieldError('telegramId')}
          </div>

          <div className="space-y-2">
            <Label htmlFor="telegramUserId">Telegram User ID</Label>
            <Input
              id="telegramUserId"
              {...createInputProps('telegramUserId')}
              placeholder="Enter Telegram numeric User ID"
              className={errors.telegramUserId ? 'border-red-300' : ''}
            />
            {renderFieldError('telegramUserId')}
          </div>

          <div className="space-y-2">
            <Label htmlFor="instagramId">Instagram Handle</Label>
            <Input
              id="instagramId"
              {...createInputProps('instagramId')}
              placeholder="Enter Instagram handle"
              className={errors.instagramId ? 'border-red-300' : ''}
            />
            {renderFieldError('instagramId')}
          </div>

          <div className="space-y-2">
            <Label htmlFor="weChatId">WeChat ID</Label>
            <Input
              id="weChatId"
              {...createInputProps('weChatId')}
              placeholder="Enter WeChat ID"
              className={errors.weChatId ? 'border-red-300' : ''}
            />
            {renderFieldError('weChatId')}
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="lineId">LINE ID</Label>
            <Input
              id="lineId"
              {...createInputProps('lineId')}
              placeholder="Enter LINE ID"
              className={errors.lineId ? 'border-red-300' : ''}
            />
            {renderFieldError('lineId')}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      {showSubmitButton && (
        <div className="flex justify-end space-x-4 pt-6">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelButtonText || 'Cancel'}
            </Button>
          )}
          <Button type="submit" disabled={!isFormValid || isLoading}>
            {showLoadingSpinner && isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {submitButtonText ||
              (isLoading
                ? mode === 'create'
                  ? 'Creating...'
                  : 'Updating...'
                : mode === 'create'
                  ? 'Create Contact'
                  : 'Update Contact')}
          </Button>
        </div>
      )}
    </form>
  )

  // Return just the form content for inline/dialog variants
  if (variant === 'inline' || variant === 'dialog') {
    return formContent
  }

  // Return wrapped in card for card variant
  return (
    <div className={className}>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === 'create' ? 'Create New Contact' : 'Edit Contact'}
          </h2>
          <p className="text-sm text-gray-600">
            {mode === 'create'
              ? 'Add a new contact to the system'
              : 'Update contact information'}
          </p>
        </div>
        <div className="px-6 py-4">{formContent}</div>
      </div>
    </div>
  )
}
